import { useCallback, useEffect, useState } from "react";
import {
    triggerScrape,
    fetchScraperRuns,
    triggerFtDkSync,
    fetchFtDkSyncs,
    triggerDeepScrape,
    fetchDeepScrapeRuns,
    triggerRefreshPoliticians,
    triggerVerifyLinks,
    fetchLinkCheckRuns,
    verifyAdminToken,
    getAdminToken,
    setAdminToken,
    fetchAdminLoginLog,
    unlockAdminIp,
    fetchPendingArticles,
    approveArticle,
    rejectArticle,
    bulkApproveArticles,
    bulkRejectArticles,
    ingestUrl,
    triggerWaybackScrape,
    fetchWaybackRuns,
    fetchAdminHealth,
    fetchParties,
    seedHistoricalArticles,
} from "@/lib/api";
import { Zap, RefreshCw, Database, Newspaper, Users, History, CheckCircle2, AlertCircle, Lock, LogOut, Shield, Unlock, LinkIcon, ClipboardList, Check, X, ExternalLink, Activity, Plus, Archive, Flame } from "lucide-react";

export default function Admin() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const t = getAdminToken();
        if (!t) {
            setChecking(false);
            return;
        }
        verifyAdminToken(t)
            .then(() => setAuthed(true))
            .catch(() => setAdminToken(null))
            .finally(() => setChecking(false));
    }, []);

    if (checking) {
        return (
            <div className="max-w-6xl mx-auto px-4 py-20 text-center text-[#4F5A7A]">
                Tjekker adgang…
            </div>
        );
    }

    if (!authed) {
        return <AdminLogin onSuccess={() => setAuthed(true)} />;
    }

    return <AdminDashboard onLogout={() => { setAdminToken(null); setAuthed(false); }} />;
}

function AdminLogin({ onSuccess }) {
    const [token, setToken] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await verifyAdminToken(token.trim());
            setAdminToken(token.trim());
            onSuccess();
        } catch (err) {
            if (err?.response?.status === 429) {
                const ra = err?.response?.headers?.["retry-after"];
                const mins = ra ? Math.ceil(parseInt(ra, 10) / 60) : 15;
                setError(`For mange forsøg. Din IP er midlertidigt låst. Prøv igen om ca. ${mins} min.`);
            } else {
                setError("Ugyldigt admin-token. Tjek venligst og prøv igen.");
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="max-w-md mx-auto px-4 py-20" data-testid="admin-login">
            <div className="bg-white border border-[#E5E8F0] rounded-2xl p-8 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-royal-soft flex items-center justify-center text-royal">
                        <Lock className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="font-serif-dm text-2xl text-royal">Admin-adgang</h1>
                        <p className="text-xs text-[#4F5A7A]">Indtast admin-token for at fortsætte</p>
                    </div>
                </div>
                <form onSubmit={submit}>
                    <input
                        type="password"
                        autoFocus
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="Indsæt admin-token…"
                        data-testid="admin-token-input"
                        className="w-full px-4 py-3 border border-[#E5E8F0] rounded-xl bg-[#F7F8FC] focus:outline-none focus:border-royal text-[#0B1A3A]"
                        required
                    />
                    {error && (
                        <p className="mt-3 text-sm text-[#BB1B2D] flex items-center gap-2" data-testid="admin-error">
                            <AlertCircle className="w-4 h-4" /> {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading || !token.trim()}
                        data-testid="admin-submit"
                        className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-colors ${
                            loading || !token.trim()
                                ? "bg-[#F0F2F8] text-[#8A94AE] cursor-not-allowed"
                                : "bg-royal text-white hover:bg-royal-deep"
                        }`}
                    >
                        {loading ? "Validerer…" : "Log ind"}
                    </button>
                </form>
                <p className="mt-6 text-xs text-[#8A94AE] leading-relaxed">
                    Token gemmes lokalt i browseren. Kun for autoriserede administratorer.
                    Kontakt systemejer for at få udleveret et token.
                </p>
            </div>
        </div>
    );
}

function AdminDashboard({ onLogout }) {
    const [loading, setLoading] = useState({});
    const [results, setResults] = useState({});
    const [scraperRuns, setScraperRuns] = useState([]);
    const [ftSyncs, setFtSyncs] = useState([]);
    const [deepRuns, setDeepRuns] = useState([]);
    const [linkRuns, setLinkRuns] = useState([]);
    const [waybackRuns, setWaybackRuns] = useState([]);
    const [loginData, setLoginData] = useState({ log: [], lockouts: [] });
    const [pending, setPending] = useState({ items: [], pending_count: 0 });
    const [health, setHealth] = useState(null);
    const [parties, setParties] = useState([]);

    // refresh-fn defineret med useCallback så useEffect kan have den i deps
    // uden infinite re-render. Alle setters fra useState og imported fetchers
    // er stabile, så funktionen er reelt konstant.
    const refresh = useCallback(() => {
        fetchScraperRuns().then(setScraperRuns).catch(() => {});
        fetchFtDkSyncs().then(setFtSyncs).catch(() => {});
        fetchDeepScrapeRuns().then(setDeepRuns).catch(() => {});
        fetchLinkCheckRuns().then(setLinkRuns).catch(() => {});
        fetchWaybackRuns().then(setWaybackRuns).catch(() => {});
        fetchAdminLoginLog().then(setLoginData).catch(() => {});
        fetchPendingArticles().then(setPending).catch(() => {});
        fetchAdminHealth().then(setHealth).catch(() => {});
    }, []);

    useEffect(() => {
        fetchParties().then(setParties).catch(() => {});
    }, []);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 30000);
        return () => clearInterval(interval);
    }, [refresh]);

    async function run(key, fn, label) {
        setLoading((s) => ({ ...s, [key]: true }));
        setResults((r) => ({ ...r, [key]: null }));
        try {
            const r = await fn();
            setResults((s) => ({ ...s, [key]: { ok: true, data: r, label } }));
        } catch (e) {
            setResults((s) => ({ ...s, [key]: { ok: false, error: e?.message || "Fejl", label } }));
        } finally {
            setLoading((s) => ({ ...s, [key]: false }));
            refresh();
        }
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="admin-page">
            <header className="mb-10 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Database className="w-5 h-5 text-royal" />
                        <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                            Dataadministration
                        </span>
                    </div>
                    <h1 className="font-serif-dm text-4xl sm:text-5xl text-royal mb-3">
                        Admin-panel
                    </h1>
                    <p className="text-[#4F5A7A] max-w-3xl leading-relaxed">
                        Manuelt triggerbare datajob. Alle job kører også automatisk dagligt på scheduler.
                        Baggrundsopgaver (dyb scrape) tager 2-3 minutter — tjek historik nedenfor for resultat.
                    </p>
                </div>
                <button
                    onClick={onLogout}
                    data-testid="admin-logout"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E8F0] text-[#4F5A7A] hover:text-royal hover:border-royal rounded-full text-sm font-semibold transition-colors"
                >
                    <LogOut className="w-4 h-4" /> Log ud
                </button>
            </header>

            {/* Action buttons */}
            <div className="grid md:grid-cols-2 gap-4 mb-10">
                <AdminAction
                    icon={Users}
                    title="Synkronisér folketingsmedlemmer"
                    description="Henter alle aktive MP'er fra ft.dk OData API og matcher til partier. Skeduleret dagligt kl. 11:55."
                    action="sync-ft"
                    loading={loading["sync-ft"]}
                    result={results["sync-ft"]}
                    onClick={() => run("sync-ft", triggerFtDkSync, "ft.dk-synkronisering")}
                />
                <AdminAction
                    icon={Newspaper}
                    title="Daglig artikel-scrape"
                    description="Henter artikler fra RSS-feeds (DR, TV2, Politiken, B.T., Ekstra Bladet) med Google News fallback. Scheduleret dagligt kl. 12:00."
                    action="scrape"
                    loading={loading["scrape"]}
                    result={results["scrape"]}
                    onClick={() => run("scrape", triggerScrape, "Scrape")}
                />
                <AdminAction
                    icon={History}
                    title="Dyb historisk scrape (år 2000+)"
                    description="Tung søgning via Google News pr. politiker for historiske artikler. Kører i baggrunden — tjek historik. Anbefales 1× pr. måned."
                    action="deep"
                    loading={loading["deep"]}
                    result={results["deep"]}
                    onClick={() => run("deep", () => triggerDeepScrape(2000), "Dyb scrape")}
                />
                <AdminAction
                    icon={RefreshCw}
                    title="Genberegn politiker-scores"
                    description="Opdaterer sandhedsscores for alle politikere ud fra nuværende artikler. Scheduleret dagligt kl. 12:10."
                    action="refresh"
                    loading={loading["refresh"]}
                    result={results["refresh"]}
                    onClick={() => run("refresh", triggerRefreshPoliticians, "Refresh")}
                />
                <AdminAction
                    icon={LinkIcon}
                    title="Tjek artikel-links (404)"
                    description="Pinger alle artikel-URLs og sletter dem der returnerer 404 / 410. Transient-fejl ignoreres. Scheduleret dagligt kl. 03:00."
                    action="verify-links"
                    loading={loading["verify-links"]}
                    result={results["verify-links"]}
                    onClick={() => run("verify-links", triggerVerifyLinks, "Link-tjek")}
                />
            </div>

            {/* Sundhedsdashboard */}
            <HealthDashboard health={health} />

            {/* Manuel URL-ingest + Wayback */}
            <DataSourceCard parties={parties} onRefresh={refresh} waybackRuns={waybackRuns} />

            {/* Moderationskø — auto-scrapede artikler venter på godkendelse */}
            <ModerationQueue
                pending={pending}
                onRefresh={refresh}
            />

            {/* Historical tables */}
            <div className="space-y-8">
                <RunsTable
                    title="ft.dk-synkronisering"
                    icon={Users}
                    runs={ftSyncs}
                    columns={[
                        { key: "timestamp", label: "Tidspunkt", format: formatDate },
                        { key: "added", label: "Tilføjet" },
                        { key: "updated", label: "Opdateret" },
                        { key: "marked_inactive", label: "Markeret inaktiv" },
                        { key: "skipped_other_party", label: "Andet parti skippet" },
                        { key: "total_fetched", label: "Hentet i alt" },
                    ]}
                    testId="ft-dk-runs"
                />
                <RunsTable
                    title="RSS-scraper"
                    icon={Newspaper}
                    runs={scraperRuns}
                    columns={[
                        { key: "timestamp", label: "Tidspunkt", format: formatDate },
                        { key: "checked", label: "Tjekket" },
                        { key: "inserted", label: "Indsat" },
                    ]}
                    testId="scraper-runs"
                />
                <RunsTable
                    title="Dyb historisk scrape"
                    icon={History}
                    runs={deepRuns}
                    columns={[
                        { key: "timestamp", label: "Tidspunkt", format: formatDate },
                        { key: "targets", label: "Politikere" },
                        { key: "checked", label: "Tjekket" },
                        { key: "inserted", label: "Indsat" },
                        { key: "earliest_year", label: "Fra år" },
                    ]}
                    testId="deep-scrape-runs"
                />

                <RunsTable
                    title="Link-tjek (404-detektion)"
                    icon={LinkIcon}
                    runs={linkRuns}
                    columns={[
                        { key: "timestamp", label: "Tidspunkt", format: formatDate },
                        { key: "checked", label: "Tjekket" },
                        { key: "deleted", label: "Slettet (404/410)" },
                        { key: "errors", label: "Transient-fejl" },
                    ]}
                    testId="link-check-runs"
                />

                <SecuritySection
                    loginData={loginData}
                    onUnlock={async (ip) => {
                        await unlockAdminIp(ip);
                        refresh();
                    }}
                />
            </div>
        </div>
    );
}

function SecuritySection({ loginData, onUnlock }) {
    const { log = [], lockouts = [] } = loginData;
    return (
        <section data-testid="security-section">
            <h2 className="flex items-center gap-2 font-serif-dm text-2xl text-royal mb-4">
                <Shield className="w-5 h-5" /> Sikkerhed: admin-login
            </h2>

            {/* Active lockouts */}
            {lockouts.length > 0 && (
                <div className="mb-5 bg-[#FDEEF0] border border-[#F5B4BE] rounded-xl p-4" data-testid="active-lockouts">
                    <div className="flex items-center gap-2 font-semibold text-[#BB1B2D] mb-3">
                        <Lock className="w-4 h-4" />
                        Aktive lockouts ({lockouts.length})
                    </div>
                    <div className="space-y-2">
                        {lockouts.map((l) => {
                            const unlockTime = l.locked_until
                                ? new Date(l.locked_until).toLocaleString("da-DK", {
                                      day: "2-digit",
                                      month: "short",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                  })
                                : "-";
                            return (
                                <div
                                    key={l.ip}
                                    className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm"
                                    data-testid={`lockout-${l.ip}`}
                                >
                                    <div>
                                        <code className="font-mono text-[#0B1A3A]">{l.ip}</code>
                                        <span className="text-[#4F5A7A] ml-3">
                                            {l.failed_count} fejl · åbner kl. {unlockTime}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => onUnlock(l.ip)}
                                        data-testid={`unlock-${l.ip}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-royal text-white text-xs font-semibold rounded-full hover:bg-royal-deep"
                                    >
                                        <Unlock className="w-3 h-3" /> Fjern lockout
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Login log */}
            <div className="overflow-x-auto bg-white border border-[#E5E8F0] rounded-2xl">
                <table className="w-full text-sm">
                    <thead className="bg-[#F7F8FC] text-[#4F5A7A]">
                        <tr>
                            <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold">Tidspunkt</th>
                            <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold">IP</th>
                            <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold">Status</th>
                            <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold">Fejl #</th>
                            <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold">User-Agent</th>
                        </tr>
                    </thead>
                    <tbody>
                        {log.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-4 py-6 text-center text-[#4F5A7A]">
                                    Ingen loginforsøg registreret endnu.
                                </td>
                            </tr>
                        ) : (
                            log.slice(0, 20).map((e, i) => (
                                <tr
                                    key={`login-${e.timestamp}-${e.ip}-${i}`}
                                    className="border-t border-[#EEF0F7] hover:bg-[#F7F8FC]/50"
                                    data-testid={`login-entry-${i}`}
                                >
                                    <td className="px-4 py-3 text-[#0B1A3A]">
                                        {new Date(e.timestamp).toLocaleString("da-DK", {
                                            day: "2-digit",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                        })}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-[#0B1A3A]">{e.ip}</td>
                                    <td className="px-4 py-3">
                                        {e.success ? (
                                            <span className="inline-flex items-center gap-1 text-[#186A3B]">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Succes
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[#BB1B2D]">
                                                <AlertCircle className="w-3.5 h-3.5" /> Fejlet
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#4F5A7A]">{e.failed_count || "—"}</td>
                                    <td className="px-4 py-3 text-[#4F5A7A] text-xs truncate max-w-[300px]" title={e.user_agent}>
                                        {e.user_agent || "—"}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <p className="mt-3 text-xs text-[#8A94AE]">
                Regler: 5 fejlede forsøg pr. IP inden for 15 min → lockout i 15 min. Lockouts kan fjernes manuelt ovenfor.
            </p>
        </section>
    );
}

function AdminAction({ icon: Icon, title, description, action, loading, result, onClick }) {
    return (
        <div className="bg-white border border-[#E5E8F0] rounded-2xl p-6 flex flex-col" data-testid={`admin-action-${action}`}>
            <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-royal-soft flex items-center justify-center text-royal flex-shrink-0">
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <h3 className="font-serif-dm text-xl text-royal leading-tight">{title}</h3>
                    <p className="text-sm text-[#4F5A7A] mt-1 leading-snug">{description}</p>
                </div>
            </div>

            <button
                onClick={onClick}
                disabled={loading}
                data-testid={`trigger-${action}`}
                className={`mt-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                    loading
                        ? "bg-[#F0F2F8] text-[#8A94AE] cursor-not-allowed"
                        : "bg-royal text-white hover:bg-royal-deep"
                }`}
            >
                {loading ? (
                    <>
                        <RefreshCw className="w-4 h-4 animate-spin" /> Kører...
                    </>
                ) : (
                    <>
                        <Zap className="w-4 h-4" /> Kør nu
                    </>
                )}
            </button>

            {result && (
                <div
                    className={`mt-3 px-3 py-2 rounded-lg text-xs flex items-start gap-2 ${
                        result.ok ? "bg-[#E8F4EC] text-[#186A3B]" : "bg-[#FDEEF0] text-[#BB1B2D]"
                    }`}
                    data-testid={`result-${action}`}
                >
                    {result.ok ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    ) : (
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                        {result.ok ? (
                            <pre className="whitespace-pre-wrap break-words">
                                {JSON.stringify(result.data, null, 1)}
                            </pre>
                        ) : (
                            <span>Fejl: {result.error}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function RunsTable({ title, icon: Icon, runs, columns, testId }) {
    return (
        <section data-testid={testId}>
            <h2 className="flex items-center gap-2 font-serif-dm text-2xl text-royal mb-4">
                <Icon className="w-5 h-5" /> Historik: {title}
            </h2>
            {runs.length === 0 ? (
                <p className="text-[#4F5A7A] text-sm bg-[#F7F8FC] border border-[#E5E8F0] rounded-xl p-4">
                    Ingen kørsler registreret endnu.
                </p>
            ) : (
                <div className="overflow-x-auto bg-white border border-[#E5E8F0] rounded-2xl">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F7F8FC] text-[#4F5A7A]">
                            <tr>
                                {columns.map((c) => (
                                    <th
                                        key={c.key}
                                        className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold"
                                    >
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {runs.slice(0, 10).map((r, i) => (
                                <tr key={r.timestamp || `run-${i}`} className="border-t border-[#EEF0F7] hover:bg-[#F7F8FC]/50">
                                    {columns.map((c) => (
                                        <td key={c.key} className="px-4 py-3 text-[#0B1A3A]">
                                            {c.format ? c.format(r[c.key]) : r[c.key] ?? "—"}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("da-DK", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function ModerationQueue({ pending, onRefresh }) {
    const [selected, setSelected] = useState({});
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const items = pending.items || [];
    const count = pending.pending_count ?? items.length;

    const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    const allSelected = items.length > 0 && selectedIds.length === items.length;

    function toggle(id) {
        setSelected((s) => ({ ...s, [id]: !s[id] }));
    }
    function toggleAll() {
        if (allSelected) {
            setSelected({});
        } else {
            const next = {};
            items.forEach((a) => { next[a.id] = true; });
            setSelected(next);
        }
    }

    async function act(fn, label) {
        setBusy(true);
        setFeedback(null);
        try {
            const res = await fn();
            const n = res?.approved ?? res?.rejected ?? 1;
            const noun = n === 1 ? "artikel" : "artikler";
            setFeedback({ ok: true, msg: `${label}: ${n} ${noun}` });
            setSelected({});
            await onRefresh();
        } catch (e) {
            setFeedback({ ok: false, msg: e?.response?.data?.detail || e?.message || "Fejl" });
        } finally {
            setBusy(false);
        }
    }

    return (
        <section
            className="mb-10 bg-gradient-to-br from-[#FFF8E8] to-white border border-[#E8D9A7] rounded-2xl p-6 shadow-sm"
            data-testid="moderation-queue"
        >
            <header className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F4E2A6] text-[#7A5A00] flex items-center justify-center">
                        <ClipboardList className="w-5 h-5" />
                    </div>
                    <div>
                        <h2 className="font-serif-dm text-2xl text-royal leading-tight">
                            Moderationskø
                        </h2>
                        <p className="text-sm text-[#4F5A7A]">
                            Auto-scrapede artikler venter på admin-godkendelse — kun godkendte vises offentligt og tæller på partiernes tillidsscore.
                        </p>
                    </div>
                </div>
                <span
                    className={`px-3 py-1.5 rounded-full text-sm font-bold tabular-nums ${
                        count > 0
                            ? "bg-[#BB1B2D] text-white"
                            : "bg-[#DCF5E4] text-[#186A3B]"
                    }`}
                    data-testid="pending-count-badge"
                >
                    {count} {count === 1 ? "afventer" : "afventer"}
                </span>
            </header>

            {feedback && (
                <div
                    className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                        feedback.ok ? "bg-[#DCF5E4] text-[#186A3B]" : "bg-[#FADDDF] text-[#8B1A1A]"
                    }`}
                    data-testid="moderation-feedback"
                >
                    {feedback.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>{feedback.msg}</span>
                </div>
            )}

            {items.length === 0 ? (
                <div className="text-center py-10 text-[#4F5A7A]" data-testid="no-pending">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-[#186A3B]" />
                    <p className="font-semibold text-royal">Køen er tom</p>
                    <p className="text-sm">Alle scrapede artikler er behandlet.</p>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                        <label className="inline-flex items-center gap-2 text-sm font-semibold text-royal cursor-pointer">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleAll}
                                data-testid="select-all-pending"
                                className="w-4 h-4 accent-royal"
                            />
                            Vælg alle ({selectedIds.length} / {items.length})
                        </label>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={busy || selectedIds.length === 0}
                                onClick={() => act(() => bulkApproveArticles(selectedIds), "Godkendt")}
                                data-testid="bulk-approve-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[#186A3B] text-white hover:bg-[#0F4D29] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Check className="w-3.5 h-3.5" />
                                Godkend {selectedIds.length || ""}
                            </button>
                            <button
                                disabled={busy || selectedIds.length === 0}
                                onClick={() => {
                                    if (!window.confirm(`Slet ${selectedIds.length} valgte artikler?`)) return;
                                    act(() => bulkRejectArticles(selectedIds), "Afvist");
                                }}
                                data-testid="bulk-reject-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[#BB1B2D] text-white hover:bg-[#8B1A1A] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <X className="w-3.5 h-3.5" />
                                Afvis {selectedIds.length || ""}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="pending-list">
                        {items.map((a) => (
                            <PendingArticleRow
                                key={a.id}
                                article={a}
                                checked={!!selected[a.id]}
                                onToggle={() => toggle(a.id)}
                                onApprove={() => act(() => approveArticle(a.id), "Godkendt")}
                                onReject={() => {
                                    if (!window.confirm("Slet denne artikel?")) return;
                                    act(() => rejectArticle(a.id), "Afvist");
                                }}
                                disabled={busy}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

const TYPE_BADGE = {
    løgn: { label: "Løgn", color: "#8B1A1A", bg: "#FADDDF" },
    valgflæsk: { label: "Valgflæsk", color: "#7A5A00", bg: "#FFF2D1" },
    skandale: { label: "Skandale", color: "#FFFFFF", bg: "#6B0F8C" },
    løfte: { label: "Løfte", color: "#186A3B", bg: "#DCF5E4" },
    generel: { label: "Omtale", color: "#364A7A", bg: "#E5EAF5" },
};

function PendingArticleRow({ article, checked, onToggle, onApprove, onReject, disabled }) {    const tb = TYPE_BADGE[article.type] || TYPE_BADGE.generel;
    return (
        <div
            className="bg-white border border-[#E5E8F0] rounded-lg px-3 py-3 flex items-start gap-3 hover:border-[#D6DCEC]"
            data-testid={`pending-row-${article.id}`}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={onToggle}
                data-testid={`pending-checkbox-${article.id}`}
                className="mt-1 w-4 h-4 accent-royal flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap text-xs">
                    <span
                        className="px-1.5 py-0.5 rounded-full font-semibold"
                        style={{ color: tb.color, backgroundColor: tb.bg }}
                    >
                        {tb.label}
                    </span>
                    <span
                        className="px-1.5 py-0.5 rounded-full font-semibold text-white"
                        style={{ backgroundColor: article.party_color || "#0C2A6B" }}
                    >
                        {article.party_short_name || article.party_id}
                    </span>
                    <span className="text-[#8A94AE]">· {article.source}</span>
                    <span className="text-[#8A94AE]">· {article.date}</span>
                    {article.related_politician_name && (
                        <span className="text-[#4F5A7A]">
                            · {article.related_politician_active === false ? "Tidl. " : ""}
                            {article.related_politician_name}
                        </span>
                    )}
                </div>
                <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-royal hover:text-danish-red text-sm leading-snug line-clamp-2 block"
                    data-testid={`pending-link-${article.id}`}
                >
                    {article.title}
                    <ExternalLink className="inline-block w-3 h-3 ml-1 opacity-60" />
                </a>
                {article.excerpt && (
                    <p className="text-xs text-[#4F5A7A] mt-1 line-clamp-2">{article.excerpt}</p>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <button
                    onClick={onApprove}
                    disabled={disabled}
                    title="Godkend"
                    data-testid={`approve-${article.id}`}
                    className="p-1.5 rounded-full bg-[#DCF5E4] text-[#186A3B] hover:bg-[#186A3B] hover:text-white disabled:opacity-40 transition-colors"
                >
                    <Check className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={onReject}
                    disabled={disabled}
                    title="Afvis (slet)"
                    data-testid={`reject-${article.id}`}
                    className="p-1.5 rounded-full bg-[#FADDDF] text-[#8B1A1A] hover:bg-[#BB1B2D] hover:text-white disabled:opacity-40 transition-colors"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}


function HealthDashboard({ health }) {
    if (!health) {
        return (
            <section className="mb-10 bg-white border border-[#E5E8F0] rounded-2xl p-6 shadow-sm" data-testid="health-dashboard">
                <p className="text-sm text-[#4F5A7A]">Indlæser sundhedsstatus…</p>
            </section>
        );
    }
    const jobs = health.jobs || {};
    const arts = health.articles || {};
    const pols = health.politicians || {};
    const days = health.articles_per_day || [];
    const max = Math.max(1, ...days.map(d => d.count));

    function jobStatus(j) {
        if (!j?.last_run) return { label: "Aldrig kørt", color: "#8A94AE", bg: "#EEF1F8" };
        const h = j.hours_ago ?? 0;
        if (h < 6) return { label: "Sund", color: "#186A3B", bg: "#DCF5E4" };
        if (h < 48) return { label: "Forsinket", color: "#7A5A00", bg: "#FFF2D1" };
        return { label: "Stale", color: "#8B1A1A", bg: "#FADDDF" };
    }

    return (
        <section
            className="mb-10 bg-white border border-[#E5E8F0] rounded-2xl p-6 shadow-sm"
            data-testid="health-dashboard"
        >
            <header className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#E5EAF5] text-royal flex items-center justify-center">
                    <Activity className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="font-serif-dm text-2xl text-royal leading-tight">Sundhedsoverblik</h2>
                    <p className="text-sm text-[#4F5A7A]">Datakilder, scraper-job og artikelvolumen</p>
                </div>
            </header>

            {/* Job-statuskort */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="health-jobs">
                {[
                    { key: "scrape", label: "RSS-scrape", icon: Newspaper },
                    { key: "ft_dk_sync", label: "ft.dk-sync", icon: Users },
                    { key: "deep_scrape", label: "Dyb scrape", icon: Database },
                    { key: "wayback", label: "Wayback", icon: Archive },
                    { key: "link_check", label: "Link-tjek", icon: LinkIcon },
                ].map(({ key, label, icon: Icon }) => {
                    const j = jobs[key] || {};
                    const s = jobStatus(j);
                    return (
                        <div
                            key={key}
                            className="border border-[#E5E8F0] rounded-xl p-3"
                            data-testid={`health-job-${key}`}
                        >
                            <div className="flex items-center justify-between mb-1.5">
                                <Icon className="w-4 h-4 text-[#4F5A7A]" />
                                <span
                                    className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                                    style={{ color: s.color, backgroundColor: s.bg }}
                                >
                                    {s.label}
                                </span>
                            </div>
                            <div className="text-xs font-semibold text-royal">{label}</div>
                            <div className="text-[11px] text-[#4F5A7A] mt-0.5">
                                {j.hours_ago != null ? `${j.hours_ago}t siden` : "—"}
                            </div>
                            {j.inserted != null && (
                                <div className="text-[11px] text-[#4F5A7A]">+{j.inserted} indsat</div>
                            )}
                            {j.error_rate != null && (
                                <div className="text-[11px] text-[#4F5A7A]">{j.error_rate}% feedfejl</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* KPI-tæller */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="health-kpis">
                {[
                    { label: "Offentlige artikler", value: arts.public, color: "text-royal" },
                    { label: "Afventer moderation", value: arts.pending, color: arts.pending > 0 ? "text-danish-red" : "text-[#186A3B]" },
                    { label: "Løgne", value: arts.loegn, color: "text-[#8B1A1A]" },
                    { label: "Aktive politikere", value: pols.active, color: "text-royal" },
                    { label: "Tidl. medlemmer", value: pols.inactive, color: "text-[#7A5A00]" },
                ].map(({ label, value, color }) => (
                    <div key={label} className="border border-[#E5E8F0] rounded-xl p-3 bg-[#F8F9FC]">
                        <div className={`text-2xl font-bold tabular-nums ${color}`}>{value ?? 0}</div>
                        <div className="text-[11px] text-[#4F5A7A] mt-1">{label}</div>
                    </div>
                ))}
            </div>

            {/* Sparkline: artikler pr. dag */}
            <div data-testid="articles-per-day">
                <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-sm font-semibold text-royal">Artikler de sidste 14 dage</h3>
                    <span className="text-xs text-[#4F5A7A]">
                        Total: {days.reduce((a, d) => a + d.count, 0)}
                    </span>
                </div>
                <div className="flex items-end gap-1 h-20 bg-[#F8F9FC] rounded-lg p-2 border border-[#E5E8F0]">
                    {days.map(d => {
                        const h = Math.max(2, Math.round((d.count / max) * 70));
                        return (
                            <div
                                key={d.date}
                                className="flex-1 rounded-t transition-all hover:opacity-80 cursor-default"
                                style={{
                                    height: `${h}px`,
                                    backgroundColor: d.count === 0 ? "#D6DCEC" : "#0C2A6B",
                                }}
                                title={`${d.date}: ${d.count}`}
                            />
                        );
                    })}
                </div>
                <div className="flex justify-between text-[10px] text-[#8A94AE] mt-1">
                    <span>{days[0]?.date.slice(5)}</span>
                    <span>{days[days.length - 1]?.date.slice(5)}</span>
                </div>
            </div>
        </section>
    );
}

function DataSourceCard({ parties, onRefresh, waybackRuns }) {
    const [url, setUrl] = useState("");
    const [partyId, setPartyId] = useState("");
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [waybackBusy, setWaybackBusy] = useState(false);
    const [waybackFromYear, setWaybackFromYear] = useState(2020);
    const [seedBusy, setSeedBusy] = useState(false);
    const [seedResult, setSeedResult] = useState(null);

    async function handleIngest(e) {
        e.preventDefault();
        if (!url.trim()) return;
        setBusy(true);
        setFeedback(null);
        try {
            const res = await ingestUrl(url.trim(), partyId || null);
            if (res.already_exists) {
                setFeedback({ ok: false, msg: "Artikel findes allerede i databasen." });
            } else {
                setFeedback({ ok: true, msg: `Tilføjet til moderationskø: ${res.title?.slice(0, 80) || "OK"}` });
                setUrl("");
                setPartyId("");
                await onRefresh();
            }
        } catch (e) {
            setFeedback({ ok: false, msg: e?.response?.data?.detail || e?.message || "Fejl" });
        } finally {
            setBusy(false);
        }
    }

    async function handleWayback() {
        if (!window.confirm(`Start Wayback Machine-scrape fra ${waybackFromYear}? Det kører i baggrunden og kan tage 5-15 min.`)) return;
        setWaybackBusy(true);
        setFeedback(null);
        try {
            const res = await triggerWaybackScrape({ from_year: waybackFromYear, to_year: 2026 });
            setFeedback({ ok: true, msg: res.message || "Wayback startet" });
            setTimeout(onRefresh, 2000);
        } catch (e) {
            setFeedback({ ok: false, msg: e?.response?.data?.detail || e?.message || "Fejl" });
        } finally {
            setWaybackBusy(false);
        }
    }

    async function handleSeedHistorical() {
        if (!window.confirm(
            "Indlæs den kuraterede liste af kendte danske politiske skandaler, " +
            "løgne og løftebrud? Operationen er idempotent — eksisterende artikler springes over."
        )) return;
        setSeedBusy(true);
        setSeedResult(null);
        setFeedback(null);
        try {
            const res = await seedHistoricalArticles();
            setSeedResult(res);
            setFeedback({
                ok: true,
                msg: `${res.inserted} nye historiske artikler indsat (${res.skipped_duplicates} duplikater sprunget over).`,
            });
            await onRefresh();
        } catch (e) {
            setFeedback({ ok: false, msg: e?.response?.data?.detail || e?.message || "Fejl" });
        } finally {
            setSeedBusy(false);
        }
    }

    const lastWayback = waybackRuns?.[0];

    return (
        <section
            className="mb-10 bg-white border border-[#E5E8F0] rounded-2xl p-6 shadow-sm"
            data-testid="data-source-card"
        >
            <header className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#E5EAF5] text-royal flex items-center justify-center">
                    <Plus className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="font-serif-dm text-2xl text-royal leading-tight">Datakilder</h2>
                    <p className="text-sm text-[#4F5A7A]">Manuel URL-ingest og Wayback-Machine arkivscrape</p>
                </div>
            </header>

            {feedback && (
                <div
                    className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                        feedback.ok ? "bg-[#DCF5E4] text-[#186A3B]" : "bg-[#FADDDF] text-[#8B1A1A]"
                    }`}
                    data-testid="datasource-feedback"
                >
                    {feedback.ok ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                    <span>{feedback.msg}</span>
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
                {/* Historisk seed (skandaler) */}
                <div
                    className="border border-[#D9B6E8] rounded-xl p-4 bg-gradient-to-br from-[#F5E8FA] to-[#FDF7FF]"
                    data-testid="historical-seed-card"
                >
                    <h3 className="text-sm font-semibold text-[#4A0A66] mb-2 flex items-center gap-1.5">
                        <Flame className="w-4 h-4" /> Historiske skandaler
                    </h3>
                    <p className="text-xs text-[#4A0A66]/80 mb-3 leading-relaxed">
                        Indlæs den kuraterede liste af kendte danske politiske skandaler,
                        løgne og løftebrud (2010-2026) fra dokumenteret pressedækning.
                        Idempotent — kan køres flere gange.
                    </p>
                    <button
                        onClick={handleSeedHistorical}
                        disabled={seedBusy}
                        data-testid="seed-historical-btn"
                        className="w-full px-4 py-2 rounded-full bg-[#6B0F8C] text-white font-semibold text-sm hover:bg-[#4A0A66] disabled:opacity-40 mb-2"
                    >
                        {seedBusy ? "Indlæser…" : "Indlæs historiske skandaler"}
                    </button>
                    {seedResult && (
                        <div
                            className="text-[11px] text-[#4A0A66] pt-2 border-t border-[#D9B6E8]"
                            data-testid="seed-historical-result"
                        >
                            <strong>{seedResult.inserted}</strong> nye / {seedResult.skipped_duplicates} duplikater
                            {seedResult.by_type && Object.keys(seedResult.by_type).length > 0 && (
                                <div className="mt-1 opacity-80">
                                    Indsat: {Object.entries(seedResult.by_type).map(([t, n]) => `${t}: ${n}`).join(" · ")}
                                </div>
                            )}
                            {seedResult.inserted === 0 && seedResult.by_type_total_in_seed && (
                                <div className="mt-1 opacity-80">
                                    Allerede til stede: {Object.entries(seedResult.by_type_total_in_seed).map(([t, n]) => `${t}: ${n}`).join(" · ")}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Manuel URL */}
                <form
                    onSubmit={handleIngest}
                    className="border border-[#E5E8F0] rounded-xl p-4 bg-[#F8F9FC]"
                    data-testid="url-ingest-form"
                >
                    <h3 className="text-sm font-semibold text-royal mb-2 flex items-center gap-1.5">
                        <Plus className="w-4 h-4" /> Tilføj artikel manuelt
                    </h3>
                    <p className="text-xs text-[#4F5A7A] mb-3">
                        Indsæt en URL til en dansk nyhedsartikel — vi henter titel, klassificerer
                        og lægger den i moderationskøen.
                    </p>
                    <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://www.dr.dk/nyheder/..."
                        required
                        disabled={busy}
                        data-testid="ingest-url-input"
                        className="w-full px-3 py-2 rounded-lg border border-[#D6DCEC] text-sm mb-2 focus:outline-none focus:border-royal"
                    />
                    <select
                        value={partyId}
                        onChange={(e) => setPartyId(e.target.value)}
                        disabled={busy}
                        data-testid="ingest-party-select"
                        className="w-full px-3 py-2 rounded-lg border border-[#D6DCEC] text-sm mb-3 bg-white focus:outline-none focus:border-royal"
                    >
                        <option value="">Auto-match parti</option>
                        {parties.map((p) => (
                            <option key={p.id} value={p.id}>
                                {`${p.short_name} — ${p.name}`}
                            </option>
                        ))}
                    </select>
                    <button
                        type="submit"
                        disabled={busy || !url.trim()}
                        data-testid="ingest-submit-btn"
                        className="w-full px-4 py-2 rounded-full bg-royal text-white font-semibold text-sm hover:bg-[#0F4D29] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? "Henter…" : "Tilføj til kø"}
                    </button>
                </form>

                {/* Wayback */}
                <div
                    className="border border-[#E5E8F0] rounded-xl p-4 bg-[#F8F9FC]"
                    data-testid="wayback-card"
                >
                    <h3 className="text-sm font-semibold text-royal mb-2 flex items-center gap-1.5">
                        <Archive className="w-4 h-4" /> Wayback Machine
                    </h3>
                    <p className="text-xs text-[#4F5A7A] mb-3">
                        Find historiske artikler om løftebrud, faktatjek og valgflæsk via
                        archive.org's CDX-index. Alternativ til Google News.
                    </p>
                    <label className="block text-xs text-[#4F5A7A] mb-1">Fra år</label>
                    <input
                        type="number"
                        value={waybackFromYear}
                        onChange={(e) => setWaybackFromYear(parseInt(e.target.value, 10) || 2020)}
                        min="2010"
                        max="2026"
                        disabled={waybackBusy}
                        data-testid="wayback-year-input"
                        className="w-full px-3 py-2 rounded-lg border border-[#D6DCEC] text-sm mb-3 focus:outline-none focus:border-royal"
                    />
                    <button
                        onClick={handleWayback}
                        disabled={waybackBusy}
                        data-testid="wayback-trigger-btn"
                        className="w-full px-4 py-2 rounded-full bg-[#0C2A6B] text-white font-semibold text-sm hover:bg-[#0A2255] disabled:opacity-40 mb-2"
                    >
                        {waybackBusy ? "Starter…" : "Start Wayback-scrape"}
                    </button>
                    {lastWayback && (
                        <div className="text-[11px] text-[#4F5A7A] pt-2 border-t border-[#E5E8F0]">
                            Sidste kørsel: {formatDate(lastWayback.timestamp)} —
                            <strong className="text-royal"> {lastWayback.inserted ?? 0}</strong> nye,
                            {lastWayback.candidates ?? 0} kandidater
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
